import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";

/**
 * ConnectorEditorDialog — Package C connector wiring editor
 * (custom API / webhook / NetSuite / QB / ShipStation).
 * Companion to ConnectorsPanel.
 * Extracted from SellerDropship.jsx in Phase 3 refactor.
 */
export default function ConnectorEditorDialog({
  connectorDraft, setConnectorDraft, saveConnector,
  connectorMeta, connectorBusy,
}) {
  return (
<Dialog open={!!connectorDraft} onOpenChange={(v) => !v && setConnectorDraft(null)}>
  <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="connector-dialog">
    <DialogHeader>
      <DialogTitle>
        {connectorDraft?.id ? "Edit" : "New"} {connectorDraft?.kind?.replace("_", " ")} connector
      </DialogTitle>
    </DialogHeader>
    {connectorDraft && (
      <div className="space-y-3 text-[12.5px]">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Name</label>
            <Input value={connectorDraft.name} onChange={e => setConnectorDraft({ ...connectorDraft, name: e.target.value })}
              data-testid="connector-name" className="h-10"/>
          </div>
          <div>
            <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Direction</label>
            <select value={connectorDraft.direction}
              onChange={e => setConnectorDraft({ ...connectorDraft, direction: e.target.value })}
              data-testid="connector-direction"
              className="w-full h-10 rounded-lg border border-border bg-white px-2 text-[12.5px]">
              <option value="push">Push (Verqo → external)</option>
              <option value="import">Import (external → Verqo)</option>
              <option value="both">Both</option>
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-[12.5px]">
          <input type="checkbox"
            checked={!!connectorDraft.is_active}
            onChange={e => setConnectorDraft({ ...connectorDraft, is_active: e.target.checked })}
            data-testid="connector-active"
            className="w-4 h-4 accent-foreground"/>
          <span>Active</span>
        </label>

        {["push", "both"].includes(connectorDraft.direction) && (
          <div>
            <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Events to push</label>
            <div className="grid grid-cols-2 gap-1">
              {connectorMeta.events.map(ev => (
                <label key={ev} className="flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-white cursor-pointer text-[12px]">
                  <input type="checkbox"
                    checked={(connectorDraft.events || []).includes(ev)}
                    onChange={e => {
                      const set = new Set(connectorDraft.events || []);
                      if (e.target.checked) set.add(ev); else set.delete(ev);
                      setConnectorDraft({ ...connectorDraft, events: Array.from(set) });
                    }}
                    data-testid={`connector-event-${ev}`}
                    className="w-4 h-4 accent-foreground"/>
                  <span className="mono">{ev}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Provider-specific fields */}
        {connectorDraft.kind === "shipstation" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">API Key</label>
              <Input value={connectorDraft.credentials?.api_key || ""}
                onChange={e => setConnectorDraft({ ...connectorDraft, credentials: { ...connectorDraft.credentials, api_key: e.target.value } })}
                data-testid="ss-key" className="h-10 mono"/>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">API Secret</label>
              <Input type="password" value={connectorDraft.credentials?.api_secret || ""}
                onChange={e => setConnectorDraft({ ...connectorDraft, credentials: { ...connectorDraft.credentials, api_secret: e.target.value } })}
                data-testid="ss-secret" className="h-10 mono"/>
            </div>
          </div>
        )}

        {connectorDraft.kind === "custom_webhook" && (
          <div className="space-y-3">
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Webhook URL</label>
              <Input value={connectorDraft.config?.url || ""}
                onChange={e => setConnectorDraft({ ...connectorDraft, config: { ...connectorDraft.config, url: e.target.value } })}
                data-testid="wh-url" placeholder="https://your-server.com/verqo-events" className="h-10 mono"/>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">HMAC secret (optional)</label>
              <Input type="password" value={connectorDraft.credentials?.hmac_secret || ""}
                onChange={e => setConnectorDraft({ ...connectorDraft, credentials: { ...connectorDraft.credentials, hmac_secret: e.target.value } })}
                data-testid="wh-secret" placeholder="Signed as X-Verqo-Signature (SHA-256)" className="h-10 mono"/>
              <div className="text-[10.5px] text-foreground/55 mt-1">
                Requests are signed as: <span className="mono">X-Verqo-Signature: hmac_sha256(secret, body)</span>
              </div>
            </div>
          </div>
        )}

        {connectorDraft.kind === "custom_api" && (
          <div className="space-y-3">
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Base URL</label>
              <Input value={connectorDraft.config?.base_url || ""}
                onChange={e => setConnectorDraft({ ...connectorDraft, config: { ...connectorDraft.config, base_url: e.target.value } })}
                data-testid="capi-base" placeholder="https://api.myerp.com/v2" className="h-10 mono"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Event path</label>
                <Input value={connectorDraft.config?.event_path || ""}
                  onChange={e => setConnectorDraft({ ...connectorDraft, config: { ...connectorDraft.config, event_path: e.target.value } })}
                  data-testid="capi-path" placeholder="/events" className="h-10 mono"/>
              </div>
              <div>
                <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Auth header name</label>
                <Input value={connectorDraft.config?.auth_header || ""}
                  onChange={e => setConnectorDraft({ ...connectorDraft, config: { ...connectorDraft.config, auth_header: e.target.value } })}
                  data-testid="capi-header" placeholder="Authorization" className="h-10 mono"/>
              </div>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Auth header value</label>
              <Input type="password" value={connectorDraft.credentials?.auth_value || ""}
                onChange={e => setConnectorDraft({ ...connectorDraft, credentials: { ...connectorDraft.credentials, auth_value: e.target.value } })}
                data-testid="capi-value" placeholder="Bearer eyJ…" className="h-10 mono"/>
            </div>
          </div>
        )}

        {["quickbooks", "netsuite"].includes(connectorDraft.kind) && (
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11.5px] text-amber-900">
              <strong>Note:</strong> {connectorDraft.kind === "quickbooks" ? "QuickBooks Online" : "NetSuite"} requires an OAuth flow. Paste the access token below to smoke-test wiring; the actual push is stubbed until we ship the OAuth handshake.
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">OAuth access token</label>
              <Input type="password" value={connectorDraft.credentials?.access_token || ""}
                onChange={e => setConnectorDraft({ ...connectorDraft, credentials: { ...connectorDraft.credentials, access_token: e.target.value } })}
                data-testid="oauth-token" className="h-10 mono"/>
            </div>
          </div>
        )}

        <Button onClick={saveConnector} disabled={connectorBusy}
          data-testid="connector-save"
          className="w-full h-10 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
          {connectorBusy ? "Saving…" : (connectorDraft.id ? "Update connector" : "Create connector")}
        </Button>
      </div>
    )}
  </DialogContent>
</Dialog>
  );
}
